// Lv3: 双方向対話（DB対応版）
import { useEffect, useRef, useState } from 'react';
import { Lv3Task, ReactionVerdict } from '../types';
import { loadSettings } from '../lib/storage';
import { speak } from '../lib/speech';
import { isSttAvailable, listen } from '../lib/stt';
import { Scheduler } from '../lib/scheduler';
import { generateLv2Message, buildLv3SystemPrompt, Stage } from '../lib/messages';
import { analyzeReply } from '../lib/claude';
import { appendReactionDb } from '../lib/db';
import DowPicker from '../components/DowPicker';
import StatusBanner from '../components/StatusBanner';
import { useTasks } from '../hooks/useTasks';
import { useAuth } from '../contexts/AuthContext';

export default function Lv3Page() {
  const { family } = useAuth();
  const { tasks, add, update, remove } = useTasks('lv3');
  const [eventTime, setEventTime] = useState('16:00');
  const [title, setTitle] = useState('習い事');
  const [dow, setDow] = useState<number[]>([]);
  const [running, setRunning] = useState(false);
  const [listening, setListening] = useState(false);
  const ignoreCountsRef = useRef<Record<string, number>>({});
  const sttAvailable = isSttAvailable();

  useEffect(() => {
    if (!family) return;
    const scheduler = new Scheduler(
      () => (tasks as Lv3Task[]),
      async ({ task, stage }) => {
        if (task.level !== 'lv3' || !stage) return;
        const lv3 = task as Lv3Task;
        const settings = loadSettings();
        const stageT = stage as Stage;
        const baseMsg = generateLv2Message(
          {
            id: lv3.id,
            level: 'lv2',
            eventTime: lv3.eventTime,
            title: lv3.title,
            daysOfWeek: lv3.daysOfWeek,
            enabled: true,
            createdAt: lv3.createdAt,
          },
          stageT
        );
        const announcement = `${baseMsg} できる？「はい」か「まだ」って言ってね。`;
        const ignoreCount = ignoreCountsRef.current[lv3.id] ?? 0;
        let reply = '';
        let verdict: ReactionVerdict = 'silent';

        try {
          const emphasis = stageT === 'pre5' || stageT === 'onTime' ? 'strong' : 'normal';
          await speak(announcement, settings, { emphasis });

          if (sttAvailable) {
            setListening(true);
            const heard = await listen(15);
            setListening(false);
            reply = heard.text;
            const sys = buildLv3SystemPrompt(lv3.title, stageT, ignoreCount);
            const analyzed = await analyzeReply(reply, lv3.title, sys, settings);
            verdict = analyzed.verdict;
            if (analyzed.reply) {
              await speak(analyzed.reply, settings, { emphasis: 'soft' });
            }
            if (verdict === 'silent' || verdict === 'negative') {
              ignoreCountsRef.current[lv3.id] = ignoreCount + 1;
            } else if (verdict === 'positive') {
              ignoreCountsRef.current[lv3.id] = Math.max(0, ignoreCount - 1);
            }
          }
        } catch (err) {
          console.error('[Lv3] 失敗', err);
          setListening(false);
        }

        await appendReactionDb(
          {
            taskId: task.id,
            taskTitle: lv3.title,
            level: 'lv3',
            firedAt: Date.now(),
            announcement,
            reply: reply || undefined,
            verdict,
            ignoreCount: ignoreCountsRef.current[lv3.id] ?? 0,
          },
          family.id
        );
      }
    );
    scheduler.start();
    setRunning(true);
    return () => {
      scheduler.stop();
      setRunning(false);
    };
  }, [family, tasks, sttAvailable]);

  const handleAdd = async () => {
    if (!eventTime || !title.trim()) return;
    await add({
      level: 'lv3',
      eventTime,
      title: title.trim(),
      daysOfWeek: dow,
      enabled: true,
    } as Omit<Lv3Task, 'id' | 'createdAt'>);
    setTitle('');
    setDow([]);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('このタスクを削除しますか？')) return;
    await remove(id);
  };

  const handleToggle = async (t: Lv3Task) => {
    await update(t.id, { enabled: !t.enabled });
  };

  const handleTryNow = async (t: Lv3Task) => {
    const settings = loadSettings();
    const announcement = `${t.title}の時間だよ！できる？「はい」か「まだ」って言ってね。`;
    try {
      await speak(announcement, settings);
      if (!sttAvailable) {
        alert('このブラウザは音声認識に対応していません（Chrome推奨）');
        return;
      }
      setListening(true);
      const heard = await listen(15);
      setListening(false);
      const sys = buildLv3SystemPrompt(t.title, 'onTime', 0);
      const analyzed = await analyzeReply(heard.text, t.title, sys, settings);
      await speak(analyzed.reply, settings, { emphasis: 'soft' });
      alert(`応答: "${heard.text || '(無音)'}"\n判定: ${analyzed.verdict}`);
    } catch (err) {
      setListening(false);
      alert(`失敗: ${err}`);
    }
  };

  const lv3Tasks = tasks as Lv3Task[];

  return (
    <>
      <StatusBanner running={running} taskCount={lv3Tasks.filter((t) => t.enabled).length} />

      {!sttAvailable && (
        <div className="alert">
          ⚠️ このブラウザは音声認識に対応していません。Chrome / Edge推奨です。
        </div>
      )}

      <div className="alert info">
        ℹ️ Lv3は段階予告 + マイクでの応答聞き取り + AI判定。
        子どもの「はい」「まだ」を理解して、次のアラート強度を調整します。
      </div>

      <div className="card">
        <h2>Lv3 タスクを追加</h2>
        <p className="description">アナウンス直後に15秒間マイクが開き、お子さんの応答を聞き取ります。</p>
        <div className="form-row-inline">
          <div className="form-row" style={{ minWidth: 140 }}>
            <label>本イベント時刻</label>
            <input type="time" value={eventTime} onChange={(e) => setEventTime(e.target.value)} />
          </div>
          <div className="form-row" style={{ flex: 1, minWidth: 200 }}>
            <label>イベント名</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例: 習い事"
            />
          </div>
        </div>
        <div className="form-row">
          <label>曜日</label>
          <DowPicker value={dow} onChange={setDow} />
        </div>
        <button className="btn btn-primary" onClick={handleAdd}>
          追加する
        </button>
      </div>

      <div className="card">
        <h2>登録済みタスク（{lv3Tasks.length}件）</h2>
        {lv3Tasks.length === 0 ? (
          <p className="muted">まだ何も登録されていません。</p>
        ) : (
          <div className="task-list">
            {lv3Tasks.map((t) => (
              <div key={t.id} className="task-item" style={{ opacity: t.enabled ? 1 : 0.5 }}>
                <div className="info">
                  <div className="time">{t.eventTime}</div>
                  <div className="msg">{t.title}</div>
                  <div className="meta">
                    {t.daysOfWeek.length === 0
                      ? '毎日'
                      : t.daysOfWeek.map((d) => '日月火水木金土'[d]).join('・')}
                    {' / 4段階予告 + 対話'}
                  </div>
                </div>
                <div className="actions">
                  <button className="btn btn-sm btn-secondary" onClick={() => handleTryNow(t)}>
                    今すぐ試す
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => handleToggle(t)}>
                    {t.enabled ? '停止' : '再開'}
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(t.id)}>
                    削除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {listening && (
        <div className="listening-overlay">
          <div className="box">
            <div className="pulse" />
            <h2>聞いています…</h2>
            <p className="muted">「はい」「やってる」「まだ」などを話してください（15秒以内）</p>
          </div>
        </div>
      )}
    </>
  );
}
